import {
  AI_TOOL_IDS,
  CONVERSION_TOOL_IDS,
  DOCUMENT_TOOL_IDS,
  EDITING_TOOL_IDS,
  PHOTO_STUDIO_TOOL_IDS,
  TRENDING_TOOL_IDS,
} from "../../config/dashboard/trendingToolsConfig";

export const INSUFFICIENT_CREDITS_BANNER =
  "Upgrade or buy credits to use this tool.";

const FREE_TOOL_KEYS = new Set([
  ...Object.values(EDITING_TOOL_IDS),
  ...Object.values(DOCUMENT_TOOL_IDS),
  ...Object.values(CONVERSION_TOOL_IDS),
]);

/** Dashboard / prep tool keys → pricing `taskKey` (`tasks.task_name`). */
export const TOOL_KEY_TO_CREDIT_TASK = {
  [AI_TOOL_IDS.BACKGROUND_IMAGE]: "ai_background",
  [AI_TOOL_IDS.UPSCALE]: "upscale",
  [AI_TOOL_IDS.EXPAND]: "outpaint",
  [AI_TOOL_IDS.SHADOW]: "ai_shadow",
  [AI_TOOL_IDS.RECOLOR]: "recolor",
  [AI_TOOL_IDS.REMOVE_OBJECT]: "remove_object",
  [AI_TOOL_IDS.REPLACE_OBJECT]: "replace_object",
  [AI_TOOL_IDS.REMOVE_TEXT]: "ai_remove_text",
  [AI_TOOL_IDS.ENHANCE]: "ai_enhance",
  [AI_TOOL_IDS.RELIGHT]: "relight",
  [AI_TOOL_IDS.REMOVE_WATERMARK]: "ai_remove_watermark",
  [AI_TOOL_IDS.BACKGROUND_REMOVER]: "remove_bg",
  [PHOTO_STUDIO_TOOL_IDS.PASSPORT_PHOTO_MAKER]: "passport_photo_low",
  [TRENDING_TOOL_IDS.EDIT_IMAGES]: "remove_bg",
  [TRENDING_TOOL_IDS.PASSPORT_PHOTOS]: "passport_photo_low",
  [TRENDING_TOOL_IDS.PRODUCT_CATALOG]: "remove_bg",
  [TRENDING_TOOL_IDS.AUTOMOBILE_CATALOG]: "remove_bg",
  [TRENDING_TOOL_IDS.FOOD_CATALOG]: "remove_bg",
};

function resolveToolKey(toolOrKey) {
  if (typeof toolOrKey === "string") return toolOrKey;
  return (
    toolOrKey?.toolKey ||
    toolOrKey?.id ||
    toolOrKey?.editorPanel ||
    null
  );
}

/** Editing + document + image conversion tools are free; AI / studio / trending need credits. */
export function toolRequiresAiCredits(toolOrKey) {
  const key = resolveToolKey(toolOrKey);
  if (!key) return true;
  return !FREE_TOOL_KEYS.has(key);
}

export function creditTaskKeyForTool(toolOrKey) {
  const key = resolveToolKey(toolOrKey);
  if (!key) return "remove_bg";
  return TOOL_KEY_TO_CREDIT_TASK[key] || key;
}

/** Prefer catalog cost; unknown tools default to 1 credit. */
export function resolveToolCreditCost(costFor, toolOrKey) {
  const taskKey = creditTaskKeyForTool(toolOrKey);
  const raw = typeof costFor === "function" ? costFor(taskKey) : null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export function insufficientCreditsBanner(toolLabel) {
  if (toolLabel && String(toolLabel).trim()) {
    return `Upgrade or buy credits to use ${String(toolLabel).trim()}.`;
  }
  return INSUFFICIENT_CREDITS_BANNER;
}

/**
 * Force Pricing gate for one tool (keeps real credit balance for others).
 * Set at build time: `REACT_APP_FORCE_CREDITS_GATE_TOOL=ai_upscale`
 * (tool key or pricing task key). Works in any REACT_APP_ENV when set.
 */
export function shouldForceCreditsGate(toolOrKey) {
  const forced = String(
    process.env.REACT_APP_FORCE_CREDITS_GATE_TOOL || ""
  )
    .trim()
    .toLowerCase();
  if (!forced) return false;
  const key = resolveToolKey(toolOrKey);
  if (!key) return forced === "remove_bg";
  const taskKey = creditTaskKeyForTool(key);
  return (
    key.toLowerCase() === forced ||
    String(taskKey).toLowerCase() === forced
  );
}
